import { useContext, useEffect, useState } from "react";
import Button from "../common/Button";
import ProfileContext from "./ProfileContext";

import { ReactComponent as PendingSvg } from "../../assets/profile/pending.svg";
import { ReactComponent as VerifiedSvg } from "../../assets/profile/verified.svg";
import { ReactComponent as ShareSvg } from "../../assets/profile/share-primary.svg";
// import { ReactComponent as EditSvg } from "../../assets/profile/edit.svg";
import { ReactComponent as EditSvg } from "../../assets/profile/edit-primary.svg";
import { ReactComponent as MessageSvg } from "../../assets/profile/message.svg";
import { ReactComponent as ClockSvg } from "../../assets/icons/clock-primary.svg";

import { differenceInDays, format } from "date-fns";
import Certification from "./Certification";
import AppContext from "../../AppContext";
import Share from "./Share";
import { useNavigate } from "react-router-dom";
import ContentLoader from "react-content-loader";
import PlanTag from "../common/PlanTag";
import Rating from "../common/Rating";
import { langComponent, PAGE_STATE } from "../../constants";
import ChatSessionContext from "../../contexts/ChatSessionContext";
import * as Tooltip from "react-tooltip";
import useUsers from "../../hooks/useUsers";
import useCompanies from "../../hooks/useCompanies";
import useWorks from "../../hooks/useWorks";

const assetsUri = import.meta.env.ASSETS_URI;

const STATE = {
  IDLE: "idle",
  LOADING: "loading",
  ERROR: "error",
};

const ProfileCard = () => {
  const [pageState, setPageState] = useState(STATE.LOADING);
  const [toggleShare, setToggleShare] = useState(false);
  const {
    user: loginUser,
    plans,
    viewProfile,
    getLang,
    isAllowed,
  } = useContext(AppContext);
  const { startChat } = useContext(ChatSessionContext);
  const Translation = getLang(langComponent.profile);
  const TranslationCommon = getLang(langComponent.common);
  const {
    isCompany,
    company,
    user,
    onEdit,
    onNewProject,
    isOwner,
    isOwnerCompany,
    isSubscribed,
    follow,
    unfollow,
    viewMode,
    setTabSelected,
    currentPlan,
  } = useContext(ProfileContext);
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState(undefined);
  const { getStatistics: getUserStatistics } = useUsers();
  const { getStatistics: getCompanyStatistics } = useCompanies();
  const { isAuth, toggleAuthModal } = useContext(AppContext);
  const [canUpload, setCanUpload] = useState(false);
  const { queryWorks } = useWorks();

  useEffect(() => {
    if (isCompany && !company) {
      setPageState(STATE.LOADING);
      return;
    }

    if (!isCompany && !user && !currentPlan) {
      setPageState(STATE.LOADING);
      return;
    }

    fetchData();
    return () => {};
  }, [user, company, currentPlan]);

  const fetchData = async () => {
    if (!isCompany && currentPlan.limit.uploads) {
      // * Get works created by user
      const { data: _works } = await queryWorks(
        { company: null, createdBy: user._id, deleted: false },
        null,
        -1,
        { _id: 1 }
      );

      setCanUpload(_works.length < currentPlan.limit.uploads);
    }

    // * Get statistics
    const statistics = isCompany
      ? await getCompanyStatistics(company._id)
      : await getUserStatistics(user._id);

    // * Get Limits
    if (!isCompany && user.status === 1) {
      setCanUpload(false);
    }

    setStatistics(statistics);
    setPageState(PAGE_STATE.IDLE);
  };

  const getRatings = () => {
    // TODO: Change to get actual rating
    const score = statistics?.score ?? 0;
    const ratings = statistics?.ratings;

    return (
      <div
        className="profile-card-statistics-ratings"
        onClick={() => setTabSelected(6)}
      >
        <Rating score={score} size={18} readOnly />
        <span className="profile-card-statistics-ratings__count">{`(${ratings})`}</span>
      </div>
    );
  };

  const getUserStatus = () => {
    if (!user) return <></>;

    // * Get user plan
    const usersPlan = plans?.find((p) => p._id === user.plan);
    const isDisplayVerified = !["Guest", "Freelancer"].includes(
      usersPlan?.name
    );

    // * Pending
    if (user.status === 1) {
      return (
        <div className="profile-card-status profile-card-status--pending">
          <PendingSvg />
          <span className="profile-card-status__desc">
            {TranslationCommon("Pending")}
          </span>
        </div>
      );
    }

    // * Rejected
    if (user.status === 3) {
      return (
        <div className="profile-card-status profile-card-status--rejected">
          <span className="profile-card-status__desc">
            {TranslationCommon("Rejected")}
          </span>
        </div>
      );
    }

    // * Approved
    if (user.status === 2 && isDisplayVerified) {
      return (
        <div className="profile-card-status profile-card-status--approved">
          <VerifiedSvg />
          <span className="profile-card-status__desc">
            {TranslationCommon("Verified")}
          </span>
        </div>
      );
    }

    return <></>;
  };

  const getUserPlan = () => {
    if (!user && !company) return <></>;

    if (isCompany) {
      return <PlanTag type={company.enterprise ? "enterprise" : "business"} />;
    }

    // * Get user plan
    const usersPlan = plans.find((p) => p._id === user.plan);

    return (
      <PlanTag
        type={usersPlan.name.toLowerCase()}
        trialEndOn={user.trialEndOn}
      />
    );
  };

  const handleShareProfile = () => {
    setToggleShare(true);
  };

  const getFollowingStatus = () => {
    if (loginUser?._id === user?._id) return;
    if (!isSubscribed) {
      return (
        <Button
          label={Translation("Follow")}
          onClick={viewMode === "guest" ? null : follow}
        />
      );
    }

    return (
      <Button
        label={Translation("Unfollow")}
        variant="secondary"
        onClick={viewMode === "guest" ? null : unfollow}
      />
    );
  };

  const handleViewAsGuestClick = () => {
    if (isCompany) {
      navigate(`/companies/${company._id}?view=guest`);
      return;
    }

    viewProfile(user._id, true);
  };

  const handleUserCompanyClick = () => {
    navigate(`/companies/${user.company._id}`);
  };

  const _canEditProfile = () => {
    if (isOwner && !isCompany) return true;

    if (isOwner && isCompany && isAllowed("CM-05-0000", "update")) {
      return true;
    }

    return false;
  };

  const getFieldValues = () => {
    const no = isCompany ? company.no : user.no;
    const createdOn = isCompany ? company.createdOn : user.createdOn;
    const name = isCompany ? company.name : `${user.firstname} ${user.surname}`;
    const avatar = isCompany ? company.logo : user.avatar;
    const bio = isCompany ? company.bio : user.bio;
    const service = company?.service ?? undefined;
    const address = isCompany ? company.address : user.contact?.address;
    const _company = isCompany ? null : user.company;
    const _trialEndOn = isCompany ? company.trialEndOn : user.trialEndOn;

    let trialEndsIn = undefined;

    if (_trialEndOn && (isCompany ? false : user?.planDetail?.price > 0)) {
      trialEndsIn = differenceInDays(new Date(_trialEndOn), new Date());
    }

    return {
      no,
      name,
      createdOn,
      avatar,
      bio,
      address,
      service,
      company: _company,
      trialEndsIn,
    };
  };

  const handleChatClick = () => {
    if (!isAuth) {
      toggleAuthModal(true, undefined, `/${user.uri}?chat`);
      return;
    }

    if (isCompany) {
      startChat(company._id, loginUser);
      return;
    }

    startChat(user._id, loginUser);
  };

  if (
    pageState !== STATE.IDLE ||
    (isCompany && !company) ||
    (!isCompany && (!user || !currentPlan))
  ) {
    return (
      <div className="profile-card">
        <div className="profile-card__loader">
          <ContentLoader width={120} height={130}>
            <circle cx="60" cy="60" r="60" />
          </ContentLoader>
          <ContentLoader width={100} height={23}>
            <rect x="0" y="0" rx="12" ry="12" width="100%" height="100%" />
          </ContentLoader>
          <ContentLoader width={200} height={29}>
            <rect x="0" y="0" rx="12" ry="12" width="100%" height="100%" />
          </ContentLoader>
          <ContentLoader width={50} height={57}>
            <rect x="0" y="0" rx="12" ry="12" width="100%" height="19" />
          </ContentLoader>
          <ContentLoader width={"100%"} height={110}>
            <rect x="0" y="0" rx="10" ry="10" width="80" height="20" />
            <rect x="0" y="40" rx="10" ry="10" width="100%" height="50" />
          </ContentLoader>
          <ContentLoader width={"100%"} height={220}>
            <rect x="0" y="0" rx="10" ry="10" width="80" height="20" />
            <rect x="0" y="40" rx="10" ry="10" width="100%" height="20" />
            <rect x="0" y="80" rx="10" ry="10" width="100%" height="20" />
            <rect x="0" y="120" rx="10" ry="10" width="100%" height="20" />
            <rect x="0" y="160" rx="10" ry="10" width="100%" height="20" />
          </ContentLoader>
          <ContentLoader width={"100%"} height={140}>
            <rect x="0" y="0" rx="10" ry="10" width="80" height="20" />
            <rect x="0" y="40" rx="10" ry="10" width="180" height="20" />
            <rect x="0" y="80" rx="10" ry="10" width="60" height="20" />
            <rect x="0" y="120" rx="10" ry="10" width="40" height="20" />
          </ContentLoader>
        </div>
      </div>
    );
  }

  const {
    no,
    avatar,
    name,
    bio,
    contact,
    createdOn,
    service,
    company: _company,
    trialEndsIn,
  } = getFieldValues();

  return (
    <>
      <div className="profile-card">
        {isOwner && trialEndsIn !== undefined && (
          <div className="profile-card-trial" id="clickable">
            <ClockSvg />
          </div>
        )}
        <div className="profile-card-picture">
          <img
            className="profile-card-picture__avatar"
            src={`${assetsUri}/${avatar}`}
            onError={({ target }) => {
              target.onerror = null;
              target.src = avatar;
            }}
          />
          {_company && (
            <img
              className="profile-card-picture__company"
              alt="company-logo"
              src={`${assetsUri}/${_company.logo}`}
              data-tooltip-id="my-tooltip"
              data-tooltip-content={`Part of ${_company.name}`}
              onClick={handleUserCompanyClick}
            />
          )}
        </div>
        <div className="profile-card-verification">
          <div className="profile-card-uid">{`UID #${(no || 1)
            .toString()
            .padStart(6, "0")}`}</div>
          {getUserStatus()}
          {getUserPlan()}
        </div>
        <div className="profile-card-name">{name}</div>
        {contact && (
          <h3
            className="profile-card-location"
            style={{
              color: "#9D9D9D",
              fontSize: "12px",
              margin: 0,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              fontWeight: 400,
            }}
          >
            <span className="profile-card-location__desc">
              Bangkok, Thailand
            </span>
          </h3>
        )}
        <div className="profile-card-links">
          <div className="profile-card-links-link" onClick={handleShareProfile}>
            <ShareSvg width={24} className="profile-card-links-link__icon" />
            <span className="profile-card-links-link__text">
              {Translation("Share")}
            </span>
          </div>
          {!isOwner && (
            <div className="profile-card-links-link" onClick={handleChatClick}>
              <MessageSvg
                className="profile-card-links-link__icon"
                width="24"
              />
              <span className="profile-card-links-link__text">
                {Translation("Chat")}
              </span>
            </div>
          )}
          {_canEditProfile() && (
            <div className="profile-card-links-link" onClick={onEdit}>
              <EditSvg width={22} className="profile-card-links-link__icon" />
              <span className="profile-card-links-link__text">
                {Translation("Edit")}
              </span>
            </div>
          )}
        </div>
        {bio && (
          <div className="profile-card-bio">
            <h3 className="profile-card-bio__header">
              {Translation(isCompany ? "About us" : "About me")}
            </h3>
            <div className="profile-card-bio__desc">{bio}</div>
          </div>
        )}
        <div className="profile-card-statistics">
          {service && (
            <>
              <span className="profile-card-statistics__title">
                {Translation("Service provided")}
              </span>
              <div className="profile-card-statistics__value">{service}</div>
            </>
          )}
          <span className="profile-card-statistics__title">
            {Translation("Joined")}
          </span>
          <div className="profile-card-statistics__value">
            {format(new Date(createdOn), "MMM yyyy")}
          </div>
          <span className="profile-card-statistics__title">
            {Translation("Ratings")}
          </span>
          <div className="profile-card-statistics__value">{getRatings()}</div>
          <span className="profile-card-statistics__title">
            {Translation("Views")}
          </span>
          <div className="profile-card-statistics__value">
            {statistics?.views.toLocaleString()}
          </div>
          <span className="profile-card-statistics__title">
            {Translation("Downloads")}
          </span>
          <div className="profile-card-statistics__value">
            {statistics?.downloads.toLocaleString()}
          </div>
          <span className="profile-card-statistics__title">
            {Translation("Followers")}
          </span>
          <div className="profile-card-statistics__value">
            {statistics?.followers.toLocaleString()}
          </div>
          <span className="profile-card-statistics__title">
            {Translation("Following")}
          </span>
          <div className="profile-card-statistics__value">
            {statistics?.followings.toLocaleString()}
          </div>
        </div>
        <Certification />
        {isOwner && (
          <>
            {!isCompany && (
              <div data-tooltip-id="my-tooltip" data-tooltip-content={""}>
                <Button
                  label={Translation("Add Project")}
                  onClick={onNewProject}
                  width={"100%"}
                  disabled={!canUpload}
                />
              </div>
            )}
            {isCompany && isAllowed("CM-03-0000", "create") && (
              <Button
                label={Translation("Add Project")}
                onClick={onNewProject}
              />
            )}
            <Button
              label={Translation("View as Guest")}
              variant={"secondary"}
              onClick={handleViewAsGuestClick}
            />
          </>
        )}
        {!isOwner && getFollowingStatus()}
        <Tooltip.Tooltip id="my-tooltip" />
      </div>
      {toggleShare && (
        <Share
          userId={isCompany ? company._id : user._id}
          onClose={() => setToggleShare(false)}
        />
      )}
      <Tooltip.Tooltip
        anchorSelect="#clickable"
        clickable
        className="profile-card-trial__popup"
      >
        Your trial is ending in {trialEndsIn} days
        <button className="profile-card-trial__popup__cta">Upgrade Now</button>
      </Tooltip.Tooltip>
    </>
  );
};

export default ProfileCard;
